import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';

import RequestDeviceSnForm from '../RequestDeviceSnForm';
import ActivationError from '../ActivationError';
import ActivationOfflineError from '../ActivationOfflineError';
import ActivationComplete from '../ActivationComplete';
import ActivationSelectSub from '../ActivationSelectSub';
import {
  REQUEST_DEVICE_SN,
  SELECT_SUB,
  SELECT_PLAN,
  ACTIVATION_COMPLETE,
  ACTIVATION_ERROR,
  PAYMENT_ERROR,
  PROCESSING,
  OFFLINE_ERROR,
} from './screenTypes';
import ActivationSelectPlan from '../ActivationSelectPlan';
import ActivationFailedPayment from '../ActivationFailedPayment';
import ActivationProcessing from '../ActivationProcessing';
import { isSubException } from '../../../utils/subscriptions';

const ActivationWizard = ({
  step,
  device,
  deviceSn,
  setDevice,
  setDeviceSn,
  setStep,
  setSub,
  cancelledSubs,
  notActivatedSubs,
  subsLoading,
  sub,
  products,
  subsCharges,
  activateNewLine,
  activateSubscription,
  continueActivationAfterLostConnection,
  restoredSelectedVariant,
}) => {
  const [errorTeamNotInformed, setErrorTeamNotInformed] = useState(false);
  const [paymentMethodId, setPaymentMethodId] = useState(false);
  const [processingType, setProcessingType] = useState('');

  const openProcessingScreen = (type) => {
    setProcessingType(type);
    setStep(PROCESSING);
  };

  const openDeviceSnScreen = () => {
    setDeviceSn('');
    setDevice(null);
    setErrorTeamNotInformed(false);
    setStep(REQUEST_DEVICE_SN);
  };

  const openCompleteScreen = () => setStep(ACTIVATION_COMPLETE);
  const openOfflineScreen = () => setStep(OFFLINE_ERROR);
  const openSelectPlanScreen = () => setStep(SELECT_PLAN);
  const openErrorScreen = (isTeamInformed = true) => {
    if (!isTeamInformed) {
      setErrorTeamNotInformed(true);
    }
    setStep(ACTIVATION_ERROR);
  };

  const openPaymentErrorScreen = (paymentMethodId) => {
    setPaymentMethodId(paymentMethodId);
    setStep(PAYMENT_ERROR);
  };

  const backToSetPlan = () => {
    setStep(SELECT_PLAN);
  };

  const setDeviceData = ({ device, deviceSn }) => {
    setDeviceSn(deviceSn);
    setDevice(device);
  };

  // redirect to error screen if sub was cancelled for non payment in the last 60 days
  useEffect(() => {
    if (cancelledSubs) {
      const now = dayjs();
      const NONPAYMENT_PERIOD_THRESHOLD = 60;
      const nonpaymentSub = cancelledSubs
        .filter((sub) => !isSubException(sub))
        .find((sub) => {
          return (
            sub.cancellation_reason ===
              'Nonpayment / Chargeback / Open Dispute' &&
            now.diff(dayjs(sub.cancelled_at), 'day') <
              NONPAYMENT_PERIOD_THRESHOLD
          );
        });
      if (nonpaymentSub) {
        openErrorScreen(false);
      }
    }
    // eslint-disable-next-line
  }, [cancelledSubs]);

  const renderContent = () => {
    switch (step) {
      case SELECT_SUB:
        return (
          <ActivationSelectSub
            subscriptions={notActivatedSubs}
            sub={sub}
            setSub={setSub}
            subsLoading={subsLoading}
            device={device}
            subsCharges={subsCharges}
            openCompleteScreen={openCompleteScreen}
            openErrorScreen={openErrorScreen}
            openSelectPlanScreen={openSelectPlanScreen}
            openProcessingScreen={openProcessingScreen}
            activateSubscription={activateSubscription}
          />
        );
      case SELECT_PLAN:
        return (
          <ActivationSelectPlan
            sub={sub}
            products={products}
            subsCharges={subsCharges}
            activateNewLine={activateNewLine}
            restoredSelectedVariant={restoredSelectedVariant}
            openCompleteScreen={openCompleteScreen}
            openErrorScreen={openErrorScreen}
            openPaymentErrorScreen={openPaymentErrorScreen}
            openProcessingScreen={openProcessingScreen}
            openOfflineScreen={openOfflineScreen}
          />
        );
      case ACTIVATION_COMPLETE:
        return (
          <ActivationComplete
            deviceSn={deviceSn}
            notActivatedSubs={notActivatedSubs}
            deviceInfo={device}
            openDeviceSnScreen={openDeviceSnScreen}
          />
        );
      case ACTIVATION_ERROR:
        return (
          <ActivationError
            deviceInfo={device}
            errorTeamNotInformed={errorTeamNotInformed}
          />
        );
      case PAYMENT_ERROR:
        return (
          <ActivationFailedPayment
            paymentMethodId={paymentMethodId}
            backToActivation={backToSetPlan}
          />
        );
      case OFFLINE_ERROR:
        return (
          <ActivationOfflineError
            sub={sub}
            products={products}
            subsCharges={subsCharges}
            activateNewLine={activateNewLine}
            restoredSelectedVariant={restoredSelectedVariant}
            openCompleteScreen={openCompleteScreen}
            openErrorScreen={openErrorScreen}
            openPaymentErrorScreen={openPaymentErrorScreen}
            openProcessingScreen={openProcessingScreen}
            openOfflineScreen={openOfflineScreen}
            deviceInfo={device}
            continueActivationAfterLostConnection={
              continueActivationAfterLostConnection
            }
          />
        );
      case PROCESSING:
        return <ActivationProcessing type={processingType} />;
      default:
        return (
          <RequestDeviceSnForm
            setDeviceData={setDeviceData}
            setStep={setStep}
          />
        );
    }
  };

  return renderContent();
};

export default ActivationWizard;
